import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDesignations } from "../../components/Api/GmsDesignation";
import { processEmployeeCredits, getEmployeesDueForLeaveCredit } from "../../components/Api/GmsAnnualLeaveCreditHistory";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

// Custom styles for Select components (following PayrollProcessing pattern)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session handling (following PayrollProcessing pattern)
const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

const AnnualLeaveCreditPage = () => {
  const [employeesDue, setEmployeesDue] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // Filter states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Pagination states (following BranchPage pattern)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAnnualLeaveCredit');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAnnualLeaveCredit');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAnnualLeaveCredit');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsAnnualLeaveCredit');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.business_Id === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness?.business_Id === session.businessId && !selectedBranch) {
      const branch = branches.find(b => b.branch_Id === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.business_Id);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  // Reload employees when business/branch changes
  useEffect(() => {
    if (businesses.length > 0) {
      loadEmployees();
    }
  }, [selectedBusiness, selectedBranch]);

  useEffect(() => {
    applyFilters();
  }, [employeesDue, selectedEmployee]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadEmployeesDue(),
        loadEmployees(),
        loadBusinesses(),
        loadDesignations()
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeesDue = async () => {
    try {
      const data = await getEmployeesDueForLeaveCredit();
      setEmployeesDue(data || []);
    } catch (error) {
      console.error("Error loading employees due for leave credit:", error);
      Swal.fire("Error", "Failed to load employees due for leave credit", "error");
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      if (selectedBusiness) {
        filter.employee_Business_Id = selectedBusiness.business_Id;
      }
      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.branch_Id;
      }
      // If user has session business/branch, use those if no selection
      if (!selectedBusiness && session.businessId) {
        filter.employee_Business_Id = session.businessId;
      }
      if (!selectedBranch && session.branchId && session.businessId) {
        filter.employee_Branch_Id = session.branchId;
      }

      const data = await getEmployees(filter);
      // Store full employee data for reference
      sessionStorage.setItem('allEmployeesData', JSON.stringify(data));

      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
        code: emp.employee_Code
      }));
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessOptions = data.map(b => ({
        ...b,
        value: b.business_Id,
        label: b.business_NameEn || b.business_NameAr
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const data = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      const branchOptions = data.map(b => ({
        ...b,
        value: b.branch_Id || b.Branch_Id,
        label: b.branch_NameEn || b.Branch_NameEn || b.branch_Name || b.Branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadDesignations = async () => {
    try {
      const data = await getDesignations({});
      setDesignations(data || []);
    } catch (error) {
      console.error("Error loading designations:", error);
    }
  };

  // Manual search function for employee search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.business_Id || session.businessId || null;
      const branchId = selectedBranch?.branch_Id || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        code: employee.employee_Code,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...employeesDue];

    if (selectedEmployee) {
      filtered = filtered.filter(emp => emp.employee_Id === selectedEmployee.value);
    }

    setFilteredEmployees(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleApplyFilter = () => {
    applyFilters();
  };

  const handleClearFilter = () => {
    setSelectedEmployee(null);
  };

  const handleProcessCredit = async (employeeData) => {
    try {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Process Leave Credit',
        html: `
          <div class="text-start">
            <p><strong>Employee:</strong> ${employeeData.employee_Name || 'Unknown'}</p>
            <p><strong>Employee Code:</strong> ${employeeData.employee_Code || 'N/A'}</p>
            <p><strong>Last Credit Date:</strong> ${new Date(employeeData.last_Credit_Date).toLocaleDateString()}</p>
            <p><strong>Days Overdue:</strong> ${employeeData.days_Overdue}</p>
          </div>
          <p class="mt-3">Are you sure you want to process the leave credit for this employee?</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Process Credit',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#556ee6'
      });

      if (!result.isConfirmed) {
        return;
      }

      setProcessingId(employeeData.employee_Id);

      // Check if employee has credit history to determine CreditMode
      let creditHistory = [];
      try {
        creditHistory = await getEmployeeCreditHistory(employeeData.employee_Id);
      } catch (error) {
        console.error("Error checking credit history:", error);
      }

      const hasCreditHistory = creditHistory && creditHistory.length > 0;

      // Determine CreditMode: NEW_EMPLOYEE if no credit history, INITIAL_MIGRATION if has history
      // For automatic processing, we don't pass AdjustedAnnualFullPay (NULL = automatic)
      const adjustedCredits = {
        CreditMode: hasCreditHistory ? 'INITIAL_MIGRATION' : 'NEW_EMPLOYEE'
      };

      // Process the credit (automatic processing - no manual adjustments)
      // NOTE: Procedure now handles LeaveBalanceHistories update automatically
      // No need to update balance manually - procedure does it
      const response = await processEmployeeCredits(employeeData.employee_Id, adjustedCredits);

      Swal.fire({
        title: 'Success',
        text: 'Leave credit processed successfully',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });

      // Reload the data to refresh the list
      await loadEmployeesDue();

    } catch (error) {
      console.error("Error processing credit:", error);

      // Extract the actual error message from the API response
      let errorMessage = 'Failed to process leave credit';
      if (error.response?.data) {
        // If the response data is a string (like "Credit processing blocked...")
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : error.response.data.message || error.response.data.title || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 5000
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Enhanced employee data with business and other details
  const getEnhancedEmployeeData = (employeeDue) => {
    const employee = employees.find(emp => emp.value === employeeDue.employee_Id);
    const employeeDetail = employee ? employees.find(emp => emp.value === employeeDue.employee_Id) : null;

    // Find employee full data to get business_Id and designation_Id
    const employeeFullData = employee ? (() => {
      // Get from the original employees API data
      const allEmployees = JSON.parse(sessionStorage.getItem('allEmployeesData') || '[]');
      return allEmployees.find(emp => emp.employee_Id === employeeDue.employee_Id);
    })() : null;

    const business = employeeFullData ? businesses.find(bus => bus.business_Id === employeeFullData.employee_Business_Id) : null;
    const designation = employeeFullData ? designations.find(des => des.designation_Id === employeeFullData.employee_Designation_ID) : null;

    return {
      ...employeeDue,
      employee_Name: employee?.label || 'Unknown',
      employee_Code: employee?.code || 'N/A',
      business_Name: business?.business_NameEn || 'Unknown',
      designation_Name: designation?.designation_NameEn || 'N/A'
    };
  };

  // Pagination logic (following BranchPage pattern)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Pagination component (following BranchPage pattern)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
          </li>

          {pageNumbers.map(number => (
            <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(number)}
              >
                {number}
              </button>
            </li>
          ))}

          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0">Annual Leave Credit Processing</h4>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {/* Filter Section */}
                <Form className="mb-4">
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Company</Label>
                        <Select
                          value={selectedBusiness}
                          onChange={(option) => {
                            setSelectedBusiness(option);
                            setSelectedBranch(null);
                            setSelectedEmployee(null);
                          }}
                          options={businesses}
                          isClearable
                          isSearchable
                          placeholder="Select Company"
                          className="react-select"
                          classNamePrefix="select"
                          isDisabled={!!session.businessId}
                          styles={selectStyles}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Branch</Label>
                        <Select
                          value={selectedBranch}
                          onChange={(option) => {
                            setSelectedBranch(option);
                            setSelectedEmployee(null);
                          }}
                          options={branches}
                          isClearable
                          isSearchable
                          placeholder="Select Branch"
                          className="react-select"
                          classNamePrefix="select"
                          isDisabled={!!session.branchId || !selectedBusiness}
                          styles={selectStyles}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Filter by Employee</Label>
                        <Select
                          value={selectedEmployee?.value ? employeeSearchOptions.find(opt => opt.value === selectedEmployee.value) || selectedEmployee : null}
                          onChange={(option) => {
                            setSelectedEmployee(option);
                            if (option) {
                              setEmployeeSearchOptions([]);
                            }
                          }}
                          options={employeeSearchOptions}
                          isClearable
                          isSearchable
                          isLoading={employeeSearchLoading}
                          filterOption={() => true}
                          noOptionsMessage={({ inputValue }) => {
                            if (employeeSearchLoading) return "Searching...";
                            if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                            return "No employees found";
                          }}
                          loadingMessage={() => "Searching employees..."}
                          onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              const timeout = setTimeout(() => {
                                searchEmployeesManually(inputValue);
                              }, 300);
                              setSearchTimeout(timeout);
                            }
                            return inputValue;
                          }}
                          placeholder="Type at least 3 characters to search..."
                          className="react-select"
                          classNamePrefix="select"
                          styles={selectStyles}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <div className="d-flex gap-2">
                        <Button color="primary" onClick={handleApplyFilter}>
                          Apply Filter
                        </Button>
                        <Button color="secondary" onClick={handleClearFilter}>
                          Clear Filter
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>

                {/* Summary */}
                <div className="mb-3">
                  <small className="text-muted">
                    Showing {filteredEmployees.length} of {employeesDue.length} employees due for leave credit processing
                  </small>
                </div>

                {/* Employee List Table */}
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Employee Code</th>
                        <th>Employee Name</th>
                        <th>Company Name</th>
                        <th>Designation</th>
                        <th>Last Credit Date</th>
                        <th>Days Overdue</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            <ClipLoader size={30} color="#556ee6" />
                          </td>
                        </tr>
                      ) : currentItems.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No employees found due for leave credit processing
                          </td>
                        </tr>
                      ) : (
                        currentItems.map((employeeDue, index) => {
                          const enhancedData = getEnhancedEmployeeData(employeeDue);
                          const globalIndex = indexOfFirstItem + index + 1;

                          return (
                            <tr key={employeeDue.employee_Id}>
                              <td>{globalIndex}</td>
                              <td>{enhancedData.employee_Code}</td>
                              <td>{enhancedData.employee_Name}</td>
                              <td>{enhancedData.business_Name}</td>
                              <td>{enhancedData.designation_Name}</td>
                              <td>{new Date(employeeDue.last_Credit_Date).toLocaleDateString('en-GB')}</td>
                              <td>
                                <span className={`badge ${employeeDue.days_Overdue > 0 ? 'bg-warning' : 'bg-info'}`}>
                                  {employeeDue.days_Overdue} days
                                </span>
                              </td>
                              <td>
                                {hasEditOrHigher && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleProcessCredit(enhancedData)}
                                    disabled={processingId === employeeDue.employee_Id}
                                  >
                                    {processingId === employeeDue.employee_Id ? (
                                      <>
                                        <ClipLoader size={15} color="#ffffff" />
                                        <span className="ms-1">Processing...</span>
                                      </>
                                    ) : (
                                      'Process Credit'
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {!loading && (
                  <div className="mt-3">
                    {renderPagination()}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AnnualLeaveCreditPage;